import { getAPIBaseURL } from '@/config'
import { generateFlexImage } from '@/libs/utilities/FlexImage'
import * as jisx0402 from '@/features/prefecture/jisx0402'
import type { Prefecture, UserGender } from '@/types/schema.generated'
import { computed, ref } from 'vue'
import type { MemberChatRoomListUserFragment } from './MemberChatRooms.generated'
import { useMemberChatRoomListLazyQuery } from './MemberChatRooms.generated'

import { toJisx0402Code } from '@/features/prefecture/prefecture'
import type { Match, MatchUser, Sex } from '@/types/app'

const ROOMS_PER_PAGE = 30

const imgixDomain = new URL(getAPIBaseURL('imgix')).host

export function useMemberMatches() {
  const variables = computed(() => ({
    first: ROOMS_PER_PAGE,
  }))
  const { load, result, fetchMore, refetch, error, loading } =
    useMemberChatRoomListLazyQuery(variables)

  const edges = computed(() => {
    const es = result.value?.colleagueChatRooms?.edges?.filter(e => {
      return e?.node?.match && e?.node?.user
    })

    if (!es) {
      return undefined
    }

    return es.map(e => {
      // edge がすべて nullish でないことをチェックする
      assertIsDefined(e)
      return e
    })
  })

  const matches = computed(() => {
    if (edges.value == undefined) {
      return []
    }
    return edges.value.map<Match>(e => {
      const n = e.node
      if (n.user == undefined) {
        // 前段で弾いているからここにはこないはず
        // あくまで型用の処理
        throw new Error('invalid')
      }

      return {
        id: Number(n.match.id),
        roomId: Number(n.id),
        user: buildMatchUser(n.user),
        planner: {
          id: Number(n.planner.id),
          name: n.planner.name,
          avatarImage: n.planner.avatar
            ? generateFlexImage(imgixDomain, n.planner.avatar)
            : undefined,
          chatParticipantId: undefined,
        },
        chatRoomStatus: n.status,
        hasUnrepliedMessage: false, // NOTE: UIで使わないので固定値
        latestMessage: {
          data: {
            createdAt: new Date(n.updatedAt),
          },
          isUnread: false, // NOTE: UIで使わないので固定値
        },
        userLatestReadMessageGUID: undefined,
        normalMessageChatReplyMatchSetting: {
          disabledUntil: n.match.normalMessageChatReplyMatchSetting
            .disabledUntil
            ? new Date(n.match.normalMessageChatReplyMatchSetting.disabledUntil)
            : undefined,
        },
      }
    })
  })

  const isLoadingNextPage = ref(false)

  const hasNextPage = computed(
    () => result.value?.colleagueChatRooms?.pageInfo?.hasNextPage
  )

  const initialError = computed(() =>
    error.value ? 'エラーが発生しました' : null
  )

  const mutate = async () => {
    if (result.value) {
      await refetch({
        first: result.value.colleagueChatRooms?.edges?.length ?? ROOMS_PER_PAGE,
      })
    }
  }

  const wrappedFetchMore = async () => {
    if (isLoadingNextPage.value) return

    const after = result.value?.colleagueChatRooms?.pageInfo.endCursor
    isLoadingNextPage.value = true
    await fetchMore({
      variables: {
        first: ROOMS_PER_PAGE,
        after,
      },
    })?.finally(() => {
      isLoadingNextPage.value = false
    })
  }

  return {
    load,
    data: matches,
    error: initialError,
    isLoadingNextPage,
    loading,
    mutate,
    fetchMore: wrappedFetchMore,
    hasNextPage,
  }
}

function buildMatchUser(user: MemberChatRoomListUserFragment): MatchUser {
  const sex = genderToSex(user.gender)
  const livingPrefecture = (() => {
    if (!user.livingPrefecture) {
      return ''
    }
    return (
      jisx0402.findByCode(toJisx0402Code(user.livingPrefecture))?.name ?? ''
    )
  })()
  const u: MatchUser = {
    id: Number(user.id),
    nickname: user.nickname,
    email: '',
    sex,
    livingPrefecture: livingPrefecture as Prefecture,
    address: {
      prefecture: livingPrefecture,
      city: user.address?.city,
    },
    yearOfBirth: user.birthYear,
    diagnosisResult: {
      personality: {
        // NOTE: 後のストーリーで、ユーザーのアイコンが表示されなくなるので、ちゃんとした実装はせず固定の値を返す
        type: 'A',
        title: '',
        description: '',
      },
    },
  }
  return u
}

function genderToSex(gender: UserGender): Sex {
  if (gender === 'MALE') return 'male'
  if (gender === 'FEMALE') return 'female'
  return 'noAnswer'
}

function assertIsDefined<T>(val: T): asserts val is NonNullable<T> {
  if (val === undefined || val === null) {
    throw new Error(`Expected 'val' to be defined, but received ${val}`)
  }
}
